import { Key, useEffect } from 'react'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useReviewTemplates } from 'hooks/queries/reviewTemplates/useReviewTemplates'
import { collectResults } from 'utils/query'
import { Item, Section } from '@react-stately/collections'
import { reviewTypeFromTemplate } from 'components/ReviewTemplateFormModal/ReviewTemplateForm/utils'
import { ReviewTemplate } from 'types'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ReviewType } from 'components/ReviewsWizard/types'
import { CollectionElement } from '@react-types/shared'
import { ReviewTypeIcon } from 'components/ReviewTypeIcon/ReviewTypeIcon'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Menu } from 'components/Menu/Menu'
import { Button } from 'components/Button'
import { Approval, Comment, More, ReadReceipt, Settings } from 'almanac-ui/icons'
import { useModals } from 'almanac-ui/Modal'
import { NewReviewTemplatesModal } from 'components/ReviewTemplatesModal/NewReviewTemplatesModal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { IconButton } from 'components/IconButton/IconButton'

export type Props = {
  onTemplateSelect: (template: ReviewTemplate) => void
  onDefaultTemplateSelect?: (reviewType: ReviewType) => void
  showDefaultTemplates?: boolean
  showManageAction?: boolean
  selectedKey?: Key
}

export const TemplatesDropdown = ({
  selectedKey,
  onTemplateSelect,
  onDefaultTemplateSelect,
  showDefaultTemplates = true,
  // TODO: this should always be enabled, but we're hiding it until we migrate
  // the automation modal to the new modal, so we can stack them correctly
  showManageAction = true,
}: Props) => {
  const workspaceId = useCurrentWorkspaceId()
  const reviewTemplatesQuery = useReviewTemplates({ workspaceId })
  const reviewTemplates = collectResults(reviewTemplatesQuery, (data) => data.review_templates)
  const { openModal } = useModals()
  const { isMobile } = useResponsiveDesign()

  useEffect(() => {
    const loadMore = async () => {
      if (!reviewTemplatesQuery.hasNextPage) return
      await reviewTemplatesQuery.fetchNextPage()
    }

    loadMore()
  }, [reviewTemplatesQuery.data?.pages.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleAction = (key: Key) => {
    if (key === 'manage') {
      openModal(NewReviewTemplatesModal)
      return
    }

    const requestTemplate = reviewTemplates.find(({ id }) => id === parseInt(key.toString()))
    if (requestTemplate) {
      onTemplateSelect(requestTemplate)
      return
    }

    const defaultTemplate = Object.values(ReviewType).find((type) => type === key)
    if (defaultTemplate) {
      onDefaultTemplateSelect?.(defaultTemplate)
      return
    }

    showToastMessage('There was a problem loading the request template', 'error')
  }

  const selectedTemplate = reviewTemplates.find(({ id }) => id.toString() === selectedKey)
  const selectedKeys = selectedKey ? new Set<Key>([selectedKey]) : new Set<Key>()

  return (
    <MenuTrigger aria-label="Select template">
      {isMobile ? (
        <IconButton variant="tertiary" size="md" icon={More} />
      ) : (
        <Button variant="tertiary" size="md" withChevron="down">
          {selectedTemplate?.name ?? 'Select Template'}
        </Button>
      )}
      <Menu onAction={handleAction} selectedKeys={selectedKeys} selectionMode="single">
        {
          [
            showDefaultTemplates && (
              <Section key="Default Templates" title="Default Templates">
                <Item key={ReviewType.FEEDBACK} textValue="Review Request">
                  <Comment /> Review Request
                </Item>
                <Item key={ReviewType.APPROVAL} textValue="Approval Request">
                  <Approval /> Approval Request
                </Item>
                <Item key={ReviewType.READ_RECEIPTS} textValue="Read Receipt Request">
                  <ReadReceipt /> Read Receipt Request
                </Item>
              </Section>
            ),
            reviewTemplates.length > 0 && (
              <Section key="Request Templates" title="Request Templates">
                {reviewTemplates.map((template) => (
                  <Item key={template.id.toString()} textValue={template.name}>
                    <ReviewTypeIcon reviewType={reviewTypeFromTemplate(template)} size="sm" />
                    {template.name}
                  </Item>
                ))}
              </Section>
            ),
            showManageAction && (
              <Section key="actions">
                <Item key="manage" textValue="Manage Templates">
                  <Settings /> Manage Templates
                </Item>
              </Section>
            ),
          ].filter(Boolean) as unknown as CollectionElement<object>
        }
      </Menu>
    </MenuTrigger>
  )
}
